import React, { useEffect, useState } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    Typography,
    Slider,
    Box
} from '@mui/material';

interface DescriptionMatchSettingsProps {
    open: boolean;
    sourceDescription?: string;
    initialStart?: number;
    initialLength?: number;
    onClose: () => void;
    onConfirm: (start: number, length: number) => void;
}

const DescriptionMatchSettings: React.FC<DescriptionMatchSettingsProps> = ({
    open,
    sourceDescription = '',
    initialStart = 0,
    initialLength = 3,
    onClose,
    onConfirm
}) => {
    const [start, setStart] = useState<number>(initialStart);
    const [length, setLength] = useState<number>(initialLength);

    // compute dynamic bounds based on description text
    const maxStart = sourceDescription.length > 0 ? sourceDescription.length - 1 : 0;
    const maxLength = sourceDescription.length - start;

    useEffect(() => {
        if (open) {
            setStart(initialStart);
            setLength(initialLength);
        }
    }, [open, initialStart, initialLength]);

    return (
        <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
            <DialogTitle>Description Match Settings</DialogTitle>
            <DialogContent>
                <Typography variant="body2" sx={{ mb: 2 }}>
                    Choose the character position to start matching from and the
                    minimum length required for a transaction to be considered a
                    match.
                </Typography>
                {sourceDescription && (
                    <Box sx={{ mb: 2 }}>
                        <Typography variant="caption">Source description</Typography>
                        <Typography sx={{ fontFamily: 'monospace', whiteSpace: 'pre-wrap' }}>
                            {sourceDescription}
                        </Typography>
                    </Box>
                )}

                <Typography gutterBottom>Start Position ({start})</Typography>
                <Slider
                    value={start}
                    onChange={(e, val) => {
                        const num = typeof val === 'number' ? val : val[0];
                        setStart(Math.min(Math.max(0, num), maxStart));
                        if (length > sourceDescription.length - num) {
                            setLength(sourceDescription.length - num);
                        }
                    }}
                    min={0}
                    max={maxStart}
                    valueLabelDisplay="auto"
                />

                <Typography gutterBottom>Match Length ({length})</Typography>
                <Slider
                    value={length}
                    onChange={(e, val) => {
                        const num = typeof val === 'number' ? val : val[0];
                        setLength(Math.min(Math.max(1, num), maxLength));
                    }}
                    min={1}
                    max={Math.max(1, maxLength)}
                    valueLabelDisplay="auto"
                />

                {sourceDescription && (
                    <Box sx={{ mt: 2 }}>
                        <Typography variant="caption">Preview of matched segment</Typography>
                        <Typography sx={{ fontFamily: 'monospace', whiteSpace: 'pre-wrap', background: '#fff9c4' }}>
                            {sourceDescription.substring(start, start + length)}
                        </Typography>
                    </Box>
                )}
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose}>Cancel</Button>
                <Button variant="contained" onClick={() => onConfirm(start, length)}>
                    Confirm
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default DescriptionMatchSettings;
